import { z } from 'zod';
import { CommerceStatus } from '../enums';
import {
  FREE_TEXT_REGEX,
  FREE_TEXT_MESSAGE,
  NAME_MIN,
  NAME_MAX,
  BUSINESS_NAME_MAX,
  STREET_NAME_MAX,
  DOOR_NUMBER_MAX,
  APARTMENT_NUMBER_MAX,
  CORNER_MAX,
  EXTERNAL_ID_MAX,
  TAX_NUMBER_REGEX,
  TAX_NUMBER_MESSAGE,
} from './fieldRules';

export interface Commerce {
  id: string;
  name: string;
  businessName: string;
  taxNumber?: string;
  status: CommerceStatus;
  addressId: string;
  externalId: string;
  apiCode?: string | null;
  businessKey: string;
  logo?: string;
  businessType?: string;
  url?: string;
  email?: string;
  phone?: string | null;
  enabled?: boolean;
  commerceGroupId?: string | null;
  address?: {
    id: string;
    streetName: string;
    doorNumber: string;
    apartmentNumber?: string;
    corner?: string;
    neighborhoodId?: string;
    lat?: string | number | null;
    long?: string | number | null;
    neighborhood?: {
      id: string;
      name: string;
      city?: {
        id: string;
        name: string;
        stateOrDepartment?: {
          id: string;
          name: string;
        };
      };
    };
  };
  createdAt: string;
  updatedAt: string;
}

export type CommerceApi = Commerce;

export interface NewCommerce {
  commerceGroupId?: string;
  name: string;
  businessName: string;
  taxNumber?: string;
  status: CommerceStatus;
  externalId: string;
  businessKey: string;
  addressId?: string;
  neighborhoodId?: string;
  streetName: string;
  doorNumber: string;
  apartmentNumber?: string;
  corner?: string;
  lat?: string;
  long?: string;
  logo?: string;
  url?: string;
  email?: string;
  mainBranch?: NewCommerceMainBranch;
}

export interface NewCommerceMainBranch {
  addressId?: string;
  phone?: string;
}

export interface UpdateCommerce {
  commerceGroupId?: string | null;
  name: string;
  businessName: string;
  taxNumber?: string;
  status: CommerceStatus;
  externalId: string;
  businessKey: string;
  neighborhoodId?: string;
  streetName: string;
  doorNumber: string;
  apartmentNumber?: string;
  corner?: string;
  logo?: string;
  businessType?: string;
  url?: string;
  email?: string;
  enabled?: boolean;
}

export interface CommerceFilters {
  name?: string;
  businessName?: string;
  taxNumber?: string;
  status?: CommerceStatus;
  addressId?: string;
  externalId?: string;
  businessType?: string;
  businessKey?: string;
  enabled?: boolean;
  logisticOperatorId?: string;
  commerceGroupId?: string;
  email?: string;
  phone?: string;
  /** Seller ID de Mercado Libre; se resuelve en `integrationCodes` antes de pedir el listado. */
  sellerId?: string;
  integrationCodes?: string[];
}

export const commerceSchema = z.object({
  name: z
    .string()
    .min(NAME_MIN, `El nombre debe tener al menos ${NAME_MIN} caracteres`)
    .max(NAME_MAX, `El nombre no puede exceder ${NAME_MAX} caracteres`)
    .regex(FREE_TEXT_REGEX, `El nombre ${FREE_TEXT_MESSAGE.toLowerCase()}`),
  businessName: z
    .string()
    .min(1, 'La razón social es requerida')
    .max(
      BUSINESS_NAME_MAX,
      `La razón social no puede exceder ${BUSINESS_NAME_MAX} caracteres`
    )
    .regex(
      FREE_TEXT_REGEX,
      `La razón social ${FREE_TEXT_MESSAGE.toLowerCase()}`
    ),
  // Opcional: si se deja vacío, el backend lo autogenera.
  externalId: z
    .string()
    .trim()
    .max(
      EXTERNAL_ID_MAX,
      `El ID externo no puede exceder ${EXTERNAL_ID_MAX} caracteres`
    ),
  status: z.nativeEnum(CommerceStatus, {
    errorMap: () => ({ message: 'El estado es requerido' }),
  }),
  taxNumber: z
    .string()
    .regex(TAX_NUMBER_REGEX, TAX_NUMBER_MESSAGE)
    .optional()
    .or(z.literal('')),
  businessKey: z
    .string()
    .max(100, 'La clave de negocio no puede exceder 100 caracteres')
    .optional()
    .or(z.literal('')),
  logo: z
    .string()
    .url('El logo debe ser una URL válida')
    .max(500, 'La URL del logo no puede exceder 500 caracteres')
    .optional()
    .or(z.literal('')),
  url: z
    .string()
    .url('El sitio web debe ser una URL válida')
    .max(500, 'La URL no puede exceder 500 caracteres')
    .optional()
    .or(z.literal('')),
  email: z
    .string()
    .email('El email debe ser válido')
    .max(100, 'El email no puede exceder 100 caracteres')
    .optional()
    .or(z.literal('')),
  stateId: z.string().min(1, 'El departamento es requerido'),
  cityId: z.string().min(1, 'La ciudad es requerida'),
  neighborhoodId: z.string().optional().or(z.literal('')),
  streetName: z
    .string()
    .min(1, 'El nombre de la calle es requerido')
    .max(
      STREET_NAME_MAX,
      `El nombre de la calle no puede exceder ${STREET_NAME_MAX} caracteres`
    )
    .regex(
      FREE_TEXT_REGEX,
      `El nombre de la calle ${FREE_TEXT_MESSAGE.toLowerCase()}`
    ),
  doorNumber: z
    .string()
    .min(1, 'El número de puerta es requerido')
    .max(
      DOOR_NUMBER_MAX,
      `El número de puerta no puede exceder ${DOOR_NUMBER_MAX} caracteres`
    )
    .regex(
      FREE_TEXT_REGEX,
      `El número de puerta ${FREE_TEXT_MESSAGE.toLowerCase()}`
    ),
  apartmentNumber: z
    .string()
    .max(
      APARTMENT_NUMBER_MAX,
      `El número de apartamento no puede exceder ${APARTMENT_NUMBER_MAX} caracteres`
    )
    .regex(
      FREE_TEXT_REGEX,
      `El número de apartamento ${FREE_TEXT_MESSAGE.toLowerCase()}`
    )
    .optional()
    .or(z.literal('')),
  corner: z
    .string()
    .max(CORNER_MAX, `La esquina no puede exceder ${CORNER_MAX} caracteres`)
    .regex(FREE_TEXT_REGEX, `La esquina ${FREE_TEXT_MESSAGE.toLowerCase()}`)
    .optional()
    .or(z.literal('')),
  logisticOperatorId: z.string().optional().or(z.literal('')),
  commerceGroupId: z.string().optional().or(z.literal('')),
});

const mainBranchFormSchema = z
  .object({
    sameAddressAsCommerce: z.boolean(),
    phone: z
      .string()
      .max(30, 'El teléfono de la casa central no puede exceder 30 caracteres'),
    stateId: z.string(),
    cityId: z.string(),
    streetName: z
      .string()
      .max(
        STREET_NAME_MAX,
        `La calle de la casa central no puede exceder ${STREET_NAME_MAX} caracteres`
      )
      .regex(
        FREE_TEXT_REGEX,
        `La calle de la casa central ${FREE_TEXT_MESSAGE.toLowerCase()}`
      )
      .or(z.literal('')),
    doorNumber: z
      .string()
      .max(
        DOOR_NUMBER_MAX,
        `El número de puerta de la casa central no puede exceder ${DOOR_NUMBER_MAX} caracteres`
      )
      .regex(
        FREE_TEXT_REGEX,
        `El número de puerta de la casa central ${FREE_TEXT_MESSAGE.toLowerCase()}`
      )
      .or(z.literal('')),
    apartmentNumber: z
      .string()
      .max(
        APARTMENT_NUMBER_MAX,
        `El apartamento de la casa central no puede exceder ${APARTMENT_NUMBER_MAX} caracteres`
      ),
    corner: z
      .string()
      .max(
        CORNER_MAX,
        `La esquina de la casa central no puede exceder ${CORNER_MAX} caracteres`
      ),
  })
  .superRefine((mainBranch, ctx) => {
    if (mainBranch.sameAddressAsCommerce) return;

    const requiredFields = [
      ['stateId', 'El departamento de la casa central es requerido'],
      ['cityId', 'La ciudad de la casa central es requerida'],
      ['streetName', 'La calle de la casa central es requerida'],
      ['doorNumber', 'El número de puerta de la casa central es requerido'],
    ] as const;

    for (const [field, message] of requiredFields) {
      if (!mainBranch[field].trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
      }
    }
  });

export const newCommerceFormSchema = commerceSchema.extend({
  mainBranch: mainBranchFormSchema,
});

export const updateCommerceSchema = commerceSchema.extend({
  id: z.string().uuid('El id debe ser un UUID válido'),
});

export type CommerceSchema = z.infer<typeof commerceSchema>;
export type UpdateCommerceSchema = z.infer<typeof updateCommerceSchema>;
export type NewCommerceFormSchema = z.infer<typeof newCommerceFormSchema>;

export const defaultCommerceValues: CommerceSchema = {
  name: '',
  businessName: '',
  taxNumber: '',
  status: CommerceStatus.ACTIVE,
  externalId: '',
  businessKey: '',
  stateId: '',
  cityId: '',
  neighborhoodId: '',
  streetName: '',
  doorNumber: '',
  apartmentNumber: '',
  corner: '',
  logisticOperatorId: '',
  commerceGroupId: '',
  logo: '',
  url: '',
  email: '',
};

export const defaultNewCommerceFormValues: NewCommerceFormSchema = {
  ...defaultCommerceValues,
  mainBranch: {
    sameAddressAsCommerce: false,
    phone: '',
    stateId: '',
    cityId: '',
    streetName: '',
    doorNumber: '',
    apartmentNumber: '',
    corner: '',
  },
};
